import React, { memo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  CircularProgress,
  InputAdornment,
  useTheme,
  Button,
  Chip,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  Badge,
  Collapse
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Camera as CameraIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Restaurant as LunchIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Add as AddIcon,
  AutoAwesome as AutoIcon,
  Movie as MovieIcon,
  Videocam as VideocamIcon,
  Today as TodayIcon,
  Groups as GroupsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon
} from '@mui/icons-material';

// CallsheetHeader Component
const CallsheetHeader = ({ data, onUpdate, projectLoading, projectError, styles }) => {
  const theme = useTheme();
  const [logoError, setLogoError] = React.useState('');
  const [daysError, setDaysError] = React.useState('');
  const [isDragging, setIsDragging] = React.useState(false);
  const [expandedSections, setExpandedSections] = React.useState({
    project: false,
    credits: false,
    callTimes: false
  });
  const liveRegionRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const handleUpdate = (field, value) => {
    const updatedData = { ...data, [field]: value };
    onUpdate(updatedData);
  };

  // Auto-set Lunch Time = General Call + 6 hours (only if lunch not set), debounced
  React.useEffect(() => {
    const t = setTimeout(() => {
      try {
        const gc = data?.generalCallTime;
        const currentLunch = data?.scheduleInfo?.lunchTime;
        if (!gc) return;
        if (currentLunch) return;
        const [hStr, mStr] = String(gc).split(':');
        const h = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        if (isNaN(h) || isNaN(m)) return;
        const base = new Date('2000-01-01T00:00:00');
        base.setHours(h);
        base.setMinutes(m);
        base.setHours(base.getHours() + 6);
        handleUpdateScheduleInfo('lunchTime', new Date(base));
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = 'Lunch time auto-set to six hours after general call.';
        }
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [data?.generalCallTime]);

  const handleUpdateScheduleInfo = (field, value) => {
    const current = data?.scheduleInfo || {};
    const updated = { ...current, [field]: value };
    handleUpdate('scheduleInfo', updated);
  };

  const handleLogoFile = async (file) => {
    if (!file) {
      handleUpdate('productionLogo', '');
      setLogoError('');
      return;
    }
    try {
      if (!file.type.startsWith('image/')) {
        setLogoError('Please select an image file.');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setLogoError('Image is too large (max 2MB).');
        return;
      }
      setLogoError('');
      const reader = new FileReader();
      reader.onload = () => {
        handleUpdate('productionLogo', reader.result || '');
      };
      reader.readAsDataURL(file);
    } catch {}
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleLogoFile(file);
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not Set';
    try {
      return new Date(date).toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'Not Set';
    }
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '--:--';
    try {
      if (typeof time === 'string' && time.includes(':')) {
        const [h, m] = time.split(':');
        const hour = parseInt(h);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${m} ${ampm}`;
      }
      return new Date(time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '--:--';
    }
  };

  // Reusable section header component
  const SectionHeader = ({ icon: Icon, title, subtitle, section, color = 'primary' }) => (
    <Box 
      onClick={() => toggleSection(section)}
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2,
        cursor: 'pointer',
        borderRadius: 1,
        background: theme.palette.mode === 'dark'
          ? alpha(theme.palette[color].main, 0.08)
          : alpha(theme.palette[color].main, 0.04),
        border: `1px solid ${alpha(theme.palette[color].main, 0.12)}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          background: theme.palette.mode === 'dark'
            ? alpha(theme.palette[color].main, 0.12)
            : alpha(theme.palette[color].main, 0.08),
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette[color].main, 0.15)
              : alpha(theme.palette[color].main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon sx={{ color: `${color}.main`, fontSize: 18 }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      <IconButton size="small" sx={{ color: 'text.secondary' }}>
        {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>
  );

  // Compact text field style
  const compactFieldSx = {
    '& .MuiInputLabel-root': { fontSize: '0.85rem', fontWeight: 600 },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.4)
        : alpha(theme.palette.grey[50], 0.8),
      borderRadius: 1,
      fontSize: '0.9rem',
      '& fieldset': { borderColor: alpha(theme.palette.divider, 0.2) },
      '&:hover fieldset': { borderColor: theme.palette.primary.main },
      '&.Mui-focused fieldset': { borderWidth: 2 }
    },
    '& .MuiInputBase-input': { py: 1.25 }
  };
  
  return (
    <Paper 
      elevation={0}
      sx={{ 
        ...(styles?.primarySection || {}),
        p: 0, 
        mb: 4,
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden'
      }}
    >
      {/* Live region for a11y */}
      <Box ref={liveRegionRef} aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }} />
      
      {/* ═══════════════════════════════════════════════════════════════════════
          HERO BANNER - Compact header with key info at a glance
      ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}
      >
        {/* Top Bar - Logo/Company + Title */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          borderBottom: theme.palette.mode === 'dark' 
            ? `1px solid ${alpha('#fff', 0.1)}`
            : `1px solid ${alpha('#000', 0.06)}`
        }}>
          {/* Logo or Company Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {data?.productionLogo ? (
              <Box 
                component="img" 
                src={data.productionLogo} 
                alt="Logo" 
                sx={{ 
                  height: 36, 
                  maxWidth: 100, 
                  objectFit: 'contain'
                }} 
              />
            ) : (
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme.palette.mode === 'dark' ? 'white' : 'text.primary', 
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}
              >
                {data?.company || 'Production Company'}
              </Typography>
            )}
          </Box>
          
          {/* Call Sheet Badge */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            background: theme.palette.mode === 'dark' 
              ? alpha('#fff', 0.15)
              : alpha(theme.palette.primary.main, 0.1)
          }}>
            <VideocamIcon sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'primary.main', fontSize: 16 }} />
            <Typography variant="subtitle2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'primary.main', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Call Sheet
            </Typography>
          </Box>
        </Box>

        {/* Main Hero Content */}
        <Box sx={{ px: 3, py: 2 }}>
          {/* Project Title */}
          <Typography 
            variant="h5" 
            sx={{ 
              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary', 
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {projectLoading ? (
              <CircularProgress size={20} />
            ) : (
              data?.projectTitle || 'Untitled Project'
            )}
          </Typography>

          {/* Quick Stats Row */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1.5
          }}>
            {/* Shoot Date */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              background: theme.palette.mode === 'dark' 
                ? alpha('#fff', 0.1)
                : alpha(theme.palette.grey[500], 0.08)
            }}>
              <TodayIcon sx={{ color: theme.palette.mode === 'dark' ? '#ffd700' : 'warning.main', fontSize: 18 }} />
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                  Shoot Date
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600, fontSize: '0.8rem' }}>
                  {formatDate(data?.date)}
                </Typography>
              </Box>
            </Box>

            {/* Day Counter */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              background: theme.palette.mode === 'dark' 
                ? alpha('#fff', 0.1)
                : alpha(theme.palette.grey[500], 0.08)
            }}>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                background: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.error.main, 0.2)
                  : alpha(theme.palette.error.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography sx={{ color: 'error.main', fontWeight: 800, fontSize: '0.85rem' }}>
                  {data?.shootingDay || 1}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                  Shooting Day
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600, fontSize: '0.8rem' }}>
                  of {data?.totalShootingDays || 1} Total
                </Typography>
              </Box>
            </Box>

            {/* General Call Time */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              background: theme.palette.mode === 'dark' 
                ? alpha('#fff', 0.1)
                : alpha(theme.palette.grey[500], 0.08)
            }}>
              <TimeIcon sx={{ color: theme.palette.mode === 'dark' ? '#00d2d3' : 'info.main', fontSize: 18 }} />
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                  General Call
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600, fontSize: '0.8rem' }}>
                  {formatTime(data?.generalCallTime)}
                </Typography>
              </Box>
            </Box>

            {/* Key Crew Quick View */}
            {(data?.director || data?.dop) && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                background: theme.palette.mode === 'dark' 
                  ? alpha('#fff', 0.1)
                  : alpha(theme.palette.grey[500], 0.08)
              }}>
                <GroupsIcon sx={{ color: theme.palette.mode === 'dark' ? '#a29bfe' : 'secondary.main', fontSize: 18 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                    Key Crew
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'white' : 'text.primary', fontWeight: 600, fontSize: '0.75rem' }}>
                    {[data?.director && `Dir: ${data.director.split(' ')[0]}`, data?.dop && `DP: ${data.dop.split(' ')[0]}`].filter(Boolean).join(' • ')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          EDIT SECTIONS - Collapsible panels for editing details
      ═══════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        
        {/* ─────────────────────────────────────────────────────────────────────
            SECTION 1: Project & Schedule
        ───────────────────────────────────────────────────────────────────── */}
        <Box>
          <SectionHeader 
            icon={BusinessIcon} 
            title="Project & Schedule" 
            subtitle="Production details and shooting day"
            section="project"
          />
          <Collapse in={expandedSections.project}>
            <Box sx={{ 
              mt: 1.5, 
              p: 2.5, 
              borderRadius: 1,
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.3)
                : '#fff',
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
            }}>
              <Grid container spacing={2}>
                {/* Row 1: Company + Logo */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Production Company"
                    value={data?.company || ''}
                    onChange={(e) => handleUpdate('company', e.target.value)}
                    placeholder="Enter company name"
                    sx={compactFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  {/* Logo Upload - Compact */}
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      p: 1,
                      px: 1.5,
                      border: `1px dashed ${isDragging ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
                      borderRadius: 1,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.default, 0.3)
                        : alpha(theme.palette.grey[50], 0.5),
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    <input 
                      ref={fileInputRef}
                      hidden 
                      accept="image/*" 
                      type="file" 
                      onChange={(e) => handleLogoFile(e.target.files?.[0] || null)} 
                    />
                    {data?.productionLogo ? (
                      <>
                        <Box 
                          component="img" 
                          src={data.productionLogo} 
                          alt="Logo" 
                          sx={{ height: 28, maxWidth: 60, objectFit: 'contain' }} 
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.7rem' }}>
                            Logo uploaded
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={(e) => { e.stopPropagation(); handleUpdate('productionLogo', ''); }}
                          sx={{ color: 'error.main', p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <UploadIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                          Upload Logo
                        </Typography>
                      </>
                    )}
                  </Box>
                  {logoError && (
                    <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.65rem' }}>
                      {logoError}
                    </Typography>
                  )}
                </Grid>

                {/* Row 2: Date + Day Counter */}
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Shoot Date"
                    value={data?.date || null}
                    onChange={(newValue) => handleUpdate('date', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        sx={compactFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Day"
                    type="number"
                    value={data?.shootingDay || 1}
                    onChange={(e) => {
                      const v = parseInt(e.target.value) || 1;
                      const max = data?.totalShootingDays || v;
                      if (v > max) {
                        setDaysError('Exceeds total');
                        handleUpdate('shootingDay', max);
                      } else {
                        setDaysError('');
                        handleUpdate('shootingDay', v);
                      }
                    }}
                    InputProps={{ inputProps: { min: 1 } }}
                    error={!!daysError}
                    sx={compactFieldSx}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="of Total"
                    type="number"
                    value={data?.totalShootingDays || 1}
                    onChange={(e) => {
                      const v = parseInt(e.target.value) || 1;
                      handleUpdate('totalShootingDays', v);
                      if ((data?.shootingDay || 1) > v) {
                        handleUpdate('shootingDay', v);
                      }
                    }}
                    InputProps={{ inputProps: { min: 1 } }}
                    sx={compactFieldSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        {/* ─────────────────────────────────────────────────────────────────────
            SECTION 2: Key Credits
        ───────────────────────────────────────────────────────────────────── */}
        <Box>
          <SectionHeader 
            icon={PersonIcon} 
            title="Key Credits" 
            subtitle="Director, producer, and crew leadership"
            section="credits"
            color="secondary"
          />
          <Collapse in={expandedSections.credits}>
            <Box sx={{ 
              mt: 1.5, 
              p: 2.5, 
              borderRadius: 1,
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.3)
                : '#fff',
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Director"
                    value={data?.director || ''}
                    onChange={(e) => handleUpdate('director', e.target.value)}
                    sx={compactFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Producer"
                    value={data?.producer || ''}
                    onChange={(e) => handleUpdate('producer', e.target.value)}
                    sx={compactFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Director of Photography"
                    value={data?.dop || ''}
                    onChange={(e) => handleUpdate('dop', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CameraIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                        </InputAdornment>
                      )
                    }}
                    sx={compactFieldSx}
                  />
                </Grid>

                {/* Additional Producers */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Additional Producers
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField
                      select
                      SelectProps={{ native: true }}
                      label="Type"
                      value={data?.__tmpProducerType || 'Producer'}
                      onChange={(e) => handleUpdate('__tmpProducerType', e.target.value)}
                      size="small"
                      sx={{ ...compactFieldSx, minWidth: 160 }}
                    >
                      {['Producer','Executive Producer','Co-Producer','Associate Producer','Line Producer','Supervising Producer'].map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </TextField>
                    <TextField
                      label="Name"
                      value={data?.__tmpProducerName || ''}
                      onChange={(e) => handleUpdate('__tmpProducerName', e.target.value)}
                      size="small"
                      sx={{ ...compactFieldSx, flex: 1, minWidth: 180 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const type = data?.__tmpProducerType || 'Producer';
                        const name = (data?.__tmpProducerName || '').trim();
                        if (!name) return;
                        const current = Array.isArray(data?.producers) ? data.producers : [];
                        const next = [...current, { type, name }];
                        onUpdate({ ...data, producers: next, __tmpProducerName: '' });
                      }}
                      sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, px: 2 }}
                    >
                      Add
                    </Button>
                  </Box>
                  {(Array.isArray(data?.producers) && data.producers.length > 0) && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {data.producers.map((p, idx) => (
                        <Chip
                          key={idx}
                          label={`${p.type}: ${p.name}`}
                          size="small"
                          onDelete={() => {
                            const next = data.producers.filter((_, i) => i !== idx);
                            onUpdate({ ...data, producers: next });
                          }}
                          sx={{
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            '& .MuiChip-deleteIcon': { color: 'error.main' }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        {/* ─────────────────────────────────────────────────────────────────────
            SECTION 3: Call Times
        ───────────────────────────────────────────────────────────────────── */}
        <Box>
          <SectionHeader 
            icon={TimeIcon} 
            title="Call Times" 
            subtitle="Daily schedule timing"
            section="callTimes"
            color="warning"
          />
          <Collapse in={expandedSections.callTimes}>
            <Box sx={{ 
              mt: 1.5, 
              p: 2.5, 
              borderRadius: 1,
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.3)
                : '#fff',
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TimePicker
                    label="General Call"
                    ampm
                    value={data?.generalCallTime ? new Date(`2000-01-01T${data.generalCallTime}`) : null}
                    onChange={(newValue) => {
                      if (newValue) {
                        const hours = newValue.getHours().toString().padStart(2, '0');
                        const minutes = newValue.getMinutes().toString().padStart(2, '0');
                        handleUpdate('generalCallTime', `${hours}:${minutes}`);
                      } else {
                        handleUpdate('generalCallTime', '');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        helperText="Crew call time"
                        sx={compactFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TimePicker
                    label="Shooting Call"
                    ampm
                    value={data?.scheduleInfo?.shootingCall ? new Date(data.scheduleInfo.shootingCall) : null}
                    onChange={(newValue) => handleUpdateScheduleInfo('shootingCall', newValue || null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        helperText="Camera ready"
                        sx={compactFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TimePicker
                    label="Lunch"
                    ampm
                    value={data?.scheduleInfo?.lunchTime ? new Date(data.scheduleInfo.lunchTime) : null}
                    onChange={(newValue) => handleUpdateScheduleInfo('lunchTime', newValue || null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        helperText="Auto: 6hrs after call"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <LunchIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                            </InputAdornment>
                          )
                        }}
                        sx={compactFieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Paper>
  );
};

export default memo(CallsheetHeader);
